import type { ComponentProps, HTMLAttributes, ReactNode, Ref } from 'react'
import type { VariantProps } from 'tailwind-variants'

import { CheckCircleIcon } from '@gto/icons-react/outline/check-circle'
import { ExclamationCircleIcon } from '@gto/icons-react/outline/exclamation-circle'
import { ExclamationTriangleIcon } from '@gto/icons-react/outline/exclamation-triangle'
import { InformationCircleIcon } from '@gto/icons-react/outline/information-circle'
import type { Button } from '@gto/ui/button'
import { cn } from '@gto/ui/cn'
import { useMediaQuery } from '@gto/ui/hooks/use-media-query'
import type { Link } from '@gto/ui/link'
import { Typography } from '@gto/ui/typography'

import { alertVariants } from './alert.styles'
import { AlertActions } from './alert-actions'

export type AlertActionProps = ComponentProps<typeof Button> | ComponentProps<typeof Link>

type AlertVariantProps = VariantProps<typeof alertVariants>

export type AlertSeverity = NonNullable<AlertVariantProps['severity']>

const ICON_BY_SEVERITY: Record<AlertSeverity, ReactNode> = {
  error: <ExclamationCircleIcon />,
  warning: <ExclamationTriangleIcon />,
  info: <InformationCircleIcon />,
  success: <CheckCircleIcon />,
}

export type AlertProps = Omit<HTMLAttributes<HTMLDivElement>, 'children'> &
  AlertVariantProps & {
    heading?: ReactNode
    message?: ReactNode
    children?: ReactNode
    icon?: ReactNode
    actions?: AlertActionProps[]
    onClose?: () => void
    closeButtonProps?: ComponentProps<typeof Button>
    ref?: Ref<HTMLDivElement>
  }

export const Alert = ({
  severity = 'info',
  heading,
  message,
  children,
  icon,
  actions,
  onClose,
  closeButtonProps,
  className,
  ref,
  ...rest
}: AlertProps) => {
  const isSmScreen = useMediaQuery('down', 'sm')
  const styles = alertVariants({ severity })
  const renderedIcon = icon ?? ICON_BY_SEVERITY[severity]
  const body = message ?? children

  return (
    <div
      ref={ref}
      className={cn(styles.root(), className)}
      {...rest}
      role="alert"
    >
      <span
        aria-hidden
        className={styles.icon()}
      >
        {renderedIcon}
      </span>
      <div
        className={styles.message()}
        data-tst="notification-text"
      >
        <div className={styles.messageInner()}>
          {heading !== undefined && (
            <Typography
              className={styles.title()}
              variant={isSmScreen ? 'subtitle1' : 'subtitle2'}
            >
              {heading}
            </Typography>
          )}
          {body !== undefined && (
            <Typography
              color="text.secondary"
              variant="body3"
            >
              {body}
            </Typography>
          )}
        </div>
        <AlertActions
          actions={actions}
          className={styles.actions()}
          closeButtonProps={closeButtonProps}
          isSmScreen={isSmScreen}
          onClose={onClose}
        />
      </div>
    </div>
  )
}
